import React, { memo, useMemo } from 'react';

import { Checkbox as MaterialCheckbox, FormControl, FormControlLabel } from '@mui/material';
import { Checkbox as CheckboxIcon, CheckedCheckbox } from 'assets/icons';
import _ from 'lodash';
import PropTypes from 'prop-types';

import FieldWrapper from '../FieldWrapper';
import HelperText from '../HelperText';

import { getStyles } from './Checkbox.styles';

const slotProps = {
  typography: {
    className: 'checkbox-label',
  },
};

const Checkbox = ({
  value = false,
  label = '',
  fullWidth = false,
  helperText = '',
  error = false,
  noValidate = false,
  disabled = false,
  inputProps,
  containerClassName = '',
  labelPlacement = 'end',
  customStyles,
  columns = 1,
  width,
  dataTestId = '',
  dataTestIdLabel = '',
  iconProps,
  isFieldChanged = false,
  ...props
}) => {
  const styles = useMemo(
    () => getStyles({ error, disabled, isFieldChanged, columns }),
    [error, disabled, isFieldChanged, columns],
  );

  const isChecked = (_.isBoolean(value) && value) || value === 'true';

  const extendedInputProps = useMemo(() => {
    if (inputProps && _.isObject(inputProps)) {
      return {
        ...inputProps,
        'data-test-checked': isChecked,
      };
    }

    return {
      'data-test-checked': isChecked,
    };
  }, [isChecked, inputProps]);

  const finalInputProps = useMemo(
    () => ({
      ...extendedInputProps,
      'data-novalidate': true,
    }),
    [extendedInputProps],
  );

  return (
    <FormControl
      className={containerClassName}
      columns={columns}
      component={FieldWrapper}
      customStyles={customStyles}
      error={error}
      fullWidth={fullWidth}
      width={width}
    >
      <FormControlLabel
        className="checkbox"
        control={
          <MaterialCheckbox
            checked={isChecked}
            checkedIcon={<CheckedCheckbox className="checkbox-icon" {...iconProps} />}
            className="checkbox-control"
            data-testid={dataTestId}
            disabled={disabled}
            icon={<CheckboxIcon className="checkbox-icon" {...iconProps} />}
            inputProps={noValidate ? finalInputProps : extendedInputProps}
            sx={styles.checkbox}
            type="checkbox"
            {...props}
          />
        }
        data-testid={dataTestIdLabel}
        disabled={disabled}
        label={label}
        labelPlacement={labelPlacement}
        slotProps={slotProps}
        sx={styles.controlLabel}
      />
      <HelperText error={error}>{helperText}</HelperText>
    </FormControl>
  );
};

Checkbox.propTypes = {
  value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
  isFieldChanged: PropTypes.bool,
  error: PropTypes.bool,
  noValidate: PropTypes.bool,
  fullWidth: PropTypes.bool,
  disabled: PropTypes.bool,
  label: PropTypes.string,
  helperText: PropTypes.string,
  dataTestId: PropTypes.string,
  dataTestIdLabel: PropTypes.string,
  width: PropTypes.string,
  inputProps: PropTypes.object,
  labelPlacement: PropTypes.string,
  containerClassName: PropTypes.string,
  columns: PropTypes.number,
  customStyles: PropTypes.object,
  iconProps: PropTypes.object,
};

export default memo(Checkbox);
